import { EntityListPage, type FilterField, type DataTableColumn, type CardConfig } from '@/components/common'
import { resolvePresetRange } from '@/lib/datePresets'
import { useTranslation } from '@/contexts/TranslationContext'
import { useUnit } from '@/contexts/UnitContext'
import { matchesSearch } from '@/lib/search'
import { 
  getAllGroups, 
  createGroup, 
  updateGroup, 
  deleteGroup,
  type AssetGroup,
  type AssetGroupInput
} from '@/lib/services/groups'
import { getAllCategories, type AssetCategory } from '@/lib/services/categories'
import { useState, useEffect } from 'react'

export function GroupsPage() {
  const { t } = useTranslation()
  const { currentUnit, userRole } = useUnit()
  const tg = (key: string) => t(`settings.assets.groups.${key}`)

  // Dados auxiliares
  const [categories, setCategories] = useState<AssetCategory[]>([])

  useEffect(() => {
    loadAuxData()
  }, [])

  const loadAuxData = async () => {
    const categoriesData = await getAllCategories()
    setCategories(categoriesData)
  }

  // Filter fields configuration
  const filterFields: FilterField[] = [
    {
      key: 'name',
      label: 'Nome do Grupo',
      type: 'text',
      placeholder: 'Filtrar por nome...'
    },
    {
      key: 'key',
      label: 'Chave',
      type: 'text',
      placeholder: 'Filtrar por chave...'
    },
    {
      key: 'category_id',
      label: 'Categoria',
      type: 'select',
      options: categories.map(cat => ({ value: cat.id, label: cat.name })),
      placeholder: 'Todas as categorias'
    },
    {
      key: 'is_active',
      label: 'Status',
      type: 'boolean'
    },
    {
      key: 'created_at',
      label: 'Data de Criação',
      type: 'daterange'
    }
  ]

  // Table columns configuration
  const tableColumns: DataTableColumn<AssetGroup>[] = [
    {
      key: 'name',
      label: tg('list.columns.name'),
      render: (group) => (
        <div className="flex items-center gap-3">
          <div>
            <div className="font-medium text-slate-900 dark:text-white">{group.name}</div>
            <div className="text-sm text-slate-500 dark:text-slate-400 font-mono">{group.key}</div>
          </div>
        </div>
      )
    },
    {
      key: 'category_name',
      label: tg('list.columns.category'),
      render: (group) => (
        <span className="text-sm text-slate-700 dark:text-slate-300">{group.category_name}</span>
      )
    },
    {
      key: 'is_active',
      label: tg('list.columns.status'),
      render: (group) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          group.is_active
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
            : 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300'
        }`}>
          {group.is_active ? tg('list.status.active') : tg('list.status.inactive')}
        </span>
      )
    }
  ]

  // Entity configuration
  const config = {
    entityType: 'groups',
    filterFields,
    
    loadData: async () => {
      return await getAllGroups(currentUnit?.id, currentUnit?.context_id || undefined)
    },
    
    createEntity: async (data: AssetGroupInput) => {
      if (!currentUnit?.id) throw new Error('Unit not selected')
      return await createGroup(data, currentUnit.id)
    },
    
    updateEntity: async (id: string, data: AssetGroupInput) => {
      if (!currentUnit?.id) throw new Error('Unit not selected')
      return await updateGroup(id, data, currentUnit.id)
    },
    
    deleteEntity: async (id: string) => {
      await deleteGroup(id)
    },
    
    applyFilters: (groups: AssetGroup[], searchQuery: string, filterValues: any) => {
      return groups.filter(group => {
        // Quick search
        if (searchQuery.trim()) {
          const q = searchQuery
          const matchesSearchAny = 
            matchesSearch(group.name, q) ||
            matchesSearch(group.key, q) ||
            matchesSearch(group.context_name, q) ||
            matchesSearch(group.category_name, q)

          if (!matchesSearchAny) return false
        }

        // Advanced filters
        if (filterValues.name && filterValues.name.trim()) {
          if (!matchesSearch(group.name, filterValues.name)) return false
        }

        if (filterValues.key && filterValues.key.trim()) {
          if (!matchesSearch(group.key, filterValues.key)) return false
        }

        if (filterValues.category_id && filterValues.category_id !== '') {
          if (group.category_id !== filterValues.category_id) return false
        }

        if (typeof filterValues.is_active === 'boolean') {
          if (group.is_active !== filterValues.is_active) return false
        }

        if (filterValues.created_at && (filterValues.created_at.start || filterValues.created_at.end || filterValues.created_at.preset)) {
          let start = filterValues.created_at.start || null
          let end = filterValues.created_at.end || null

          if (filterValues.created_at.preset) {
            const resolved = resolvePresetRange(filterValues.created_at.preset)
            start = resolved.start
            end = resolved.end
          }

          const groupDate = new Date(group.created_at).toISOString().split('T')[0]
          if (start && groupDate < start) return false
          if (end && groupDate > end) return false
        }

        return true
      })
    },
    
    validateForm: (data: AssetGroupInput) => {
      const errors: Record<string, string> = {}
      
      if (!data.name.trim()) errors.name = tg('messages.nameRequired')
      else if (data.name.trim().length < 2) errors.name = tg('messages.nameMinLength')
      else if (data.name.trim().length > 100) errors.name = tg('messages.nameMaxLength')
      
      if (!data.key.trim()) errors.key = tg('messages.keyRequired')
      else if (!/^[a-z0-9_]+$/.test(data.key)) errors.key = tg('messages.keyInvalid')
      else if (data.key.length < 2) errors.key = tg('messages.keyMinLength')
      else if (data.key.length > 50) errors.key = tg('messages.keyMaxLength')
      
      if (!data.context_id) errors.context_id = tg('messages.contextRequired')
      if (!data.category_id) errors.category_id = tg('messages.categoryRequired')
      
      return errors
    }
  }

  // Labels
  const labels = {
    title: tg('list.title'),
    description: tg('list.description'),
    newButton: tg('actions.new'),
    searchPlaceholder: tg('filters.searchPlaceholder'),
    emptyTitle: tg('list.empty.title'),
    emptyDescription: tg('list.empty.description'),
    emptyActionLabel: tg('actions.new'),
    modalTitleNew: tg('record.new'),
    modalTitleEdit: tg('record.edit'),
    modalTitleView: tg('actions.view'),
    recordTitle: tg('record.title'),
    deleteTitle: tg('record.delete'),
    deleteMessage: tg('record.deleteConfirm'),
    deleteButton: tg('actions.delete'),
    cancelButton: tg('actions.cancel'),
    submitButton: tg('actions.create'),
    updateButton: tg('actions.update'),
    viewCard: tg('filters.viewCard'),
    viewTable: tg('filters.viewTable'),
    clearSearch: tg('filters.clear')
  }

  // Icon
  const icon = (
    <svg className="h-7 w-7 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  )

  // Card configuration (replaces manual renderCard - enables automatic customization!)
  const cardConfig: CardConfig<AssetGroup> = {
    getIcon: () => (
      <svg className="h-5 w-5 text-slate-700 dark:text-slate-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
      </svg>
    ),
    getTitle: (group) => group.name,
    getSubtitle: (group) => group.key,
    getBadge: (group) => ({
      label: group.is_active ? tg('list.status.active') : tg('list.status.inactive'),
      variant: group.is_active ? 'success' : 'info'
    }),
    fields: [
      {
        key: 'category',
        label: tg('list.columns.category'),
        getValue: (group) => group.category_name || '-'
      },
      {
        key: 'key',
        label: 'Chave',
        getValue: (group) => group.key
      },
      {
        key: 'status',
        label: tg('list.columns.status'),
        getValue: (group) => group.is_active ? tg('list.status.active') : tg('list.status.inactive')
      },
      {
        key: 'created',
        label: 'Data de Criação',
        getValue: (group) => new Date(group.created_at).toLocaleDateString('pt-BR')
      }
    ],
    editLabel: tg('actions.edit'),
    deleteLabel: tg('actions.delete')
  }

  // Form render
  const renderForm = (
    formData: AssetGroupInput,
    setFormData: (data: AssetGroupInput) => void,
    formErrors: Record<string, string>,
    submitting: boolean,
    editingEntity: AssetGroup | null,
    mode: 'create' | 'edit' | 'view'
  ) => (
    <div className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tg('record.fields.name')}
        </label>
        <input
          type="text"
          id="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder={tg('record.fields.namePlaceholder')}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.name ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          disabled={submitting || mode === 'view'}
        />
        {formErrors.name && <p className="mt-1 text-sm text-red-600">{formErrors.name}</p>}
      </div>

      <div>
        <label htmlFor="key" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tg('record.fields.key')}
        </label>
        <input
          type="text"
          id="key"
          value={formData.key}
          onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase() })}
          placeholder={tg('record.fields.keyPlaceholder')}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.key ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono`}
          disabled={submitting || editingEntity?.is_system || mode === 'view'}
        />
        {formErrors.key && <p className="mt-1 text-sm text-red-600">{formErrors.key}</p>}
        <p className="mt-1 text-xs text-slate-500">{tg('record.fields.keyHelp')}</p>
      </div>

      <div>
        <label htmlFor="category_id" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
          {tg('record.fields.category')}
        </label>
        <select
          id="category_id"
          value={formData.category_id}
          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
          className={`w-full px-4 py-2 rounded-lg border ${
            formErrors.category_id ? 'border-red-300' : 'border-slate-300'
          } dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500`}
          disabled={submitting || mode === 'view'}
        >
          <option value="">{tg('record.fields.categoryPlaceholder')}</option>
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        {formErrors.category_id && <p className="mt-1 text-sm text-red-600">{formErrors.category_id}</p>}
      </div>

      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="is_active"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-emerald-600 focus:ring-emerald-500"
          disabled={submitting || mode === 'view'}
        />
        <label htmlFor="is_active" className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {tg('record.fields.isActive')}
        </label>
      </div>
    </div>
  )

  return (
    <EntityListPage<AssetGroup, AssetGroupInput>
      config={config}
      labels={labels}
      icon={icon}
      filterFields={filterFields}
      tableColumns={tableColumns}
      userRole={userRole}
      actionsColumnLabel={tg('record.fields.actions')}
      canEdit={(group) => !group.is_system}
      canDelete={(group) => !group.is_system}
      cardConfig={cardConfig}
      renderForm={renderForm}
      entityToFormData={(group) => ({
        name: group.name,
        key: group.key,
        context_id: group.context_id,
        category_id: group.category_id,
        is_active: group.is_active ?? true
      })}
      emptyFormData={() => ({
        name: '',
        key: '',
        context_id: currentUnit?.context_id || '',
        category_id: '',
        is_active: true
      })}
          onKanbanMove={async (entity, newColumn) => {
            try {
              if (!currentUnit?.id) throw new Error('Unit not selected')
              const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
              const sl = normalize(newColumn || '')
              const isActive = ['ativo','active','true','sim','yes','1','verdadeiro'].includes(sl)

              const input: AssetGroupInput = {
                name: (entity as any).name,
                key: (entity as any).key,
                context_id: (entity as any).context_id,
                category_id: (entity as any).category_id,
                is_active: isActive
              }

              await updateGroup((entity as any).id, input, currentUnit.id)
            } catch (err) {
              console.error('Erro ao atualizar status via Kanban:', err)
              throw err
            }
          }}
          namespace="settings.assets.groups"
    />
  )
}
